"use client";
import { LegalMenu } from "./LegalMenu/LegalMenu";
import { MenuType } from "../../types/baseTypes";
import { useState } from "react";
import { customSanitize } from "../../utils/customSanitize";
import {
  optionsFooterColumnsType,
  OptionsFooterType,
  OptionsSocialMediaType,
} from "@/types/OptionType";
import styles from "./Footer.module.scss";
import { Contact } from "./Contact/Contact";
import { Socials } from "./Socials/Socials";
import FooterMenu from "./FooterMenu/FooterMenu";
import { ConsentManagement } from "@aleksundshantu/aunds-cookie-plugin-react";
import { passACFLink } from "@/utils/passACFLink";
import { Section } from "../Section/Section";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { CustomLink } from "../CustomLink";
import { MediaItemResponsive } from "../MediaItem/MediaItemResponsive";
import { FooterAccordionType, PromoBoxType } from "@/types/OptionType";
import { ExtendedLink } from "../ExtededLink/ExtendedLink";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
import { useRouter } from "next/compat/router";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FooterLogoImage = ({ logo, isSVG }: { logo: any; isSVG: boolean }) => {
  return isSVG ? (
    <CustomSVG src={logo.url} width={"100%"} height={"100%"} />
  ) : (
    <MediaItemResponsive
      image_desktop={logo}
      sizes="(max-width: 1023px) 100vw, (max-width: 1920px) 25vw, 310px"
      className={styles["logo"]}
      objectFit="contain"
    />
  );
};

// TODO and only pass props that are actually used not whole options object and not whoel menu object
interface FooterProps {
  legalMenu?: MenuType;
  footerMenu?: MenuType;
  autoShowConsentManagement?: boolean;
  footerOptions: OptionsFooterType;
  socialMedia: OptionsSocialMediaType;
}

export const Footer = ({
  legalMenu,
  footerMenu,
  socialMedia,
  footerOptions,

  autoShowConsentManagement,
}: FooterProps) => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [showConsentManagement, setShowConsentManagement] = useState(false);
  return (
    <DesktopFooter
      legalMenu={legalMenu}
      footerMenu={footerMenu}
      socialMedia={socialMedia}
      footerOptions={footerOptions}
      autoShowConsentManagement={autoShowConsentManagement}
    />
  );
};

const FooterColumn = ({
  type,
  columnData,
  footerMenu,
  socialMedia,
}: {
  type:
    | "logos"
    | "contact"
    | "navigation-links"
    | "social-icons"
    | "social-icons-with-links";
  columnData: optionsFooterColumnsType;
  footerMenu: MenuType | undefined;
  socialMedia: OptionsSocialMediaType;
}) => {
  switch (type) {
    case "logos": {
      return (
        <div className={`${styles["footer-logos"]}`}>
          {Array.isArray(columnData.logos_footer) &&
            columnData.logos_footer.map((el, i) => {
              const { logo, link } = el;
              const isSVG = Boolean(logo.mime_type?.includes("svg"));
              return link && link.url ? (
                <CustomLink
                  key={"footer-logo-" + i}
                  className={`${styles["footer-logo"]}`}
                  {...passACFLink(el, "link")}
                >
                  <FooterLogoImage logo={logo} isSVG={isSVG} />
                </CustomLink>
              ) : (
                <div
                  key={"footer-logo-" + i}
                  className={`${styles["footer-logo"]}`}
                >
                  <FooterLogoImage logo={logo} isSVG={isSVG} />
                </div>
              );
            })}
        </div>
      );
    }
    case "contact": {
      return (
        <div className={`${styles["footer-contact"]}`}>
          <Contact
            contact={{
              title: columnData.contact.title,
              content: columnData.contact.content,
              link: columnData.contact.link,
            }}
          />
        </div>
      );
    }
    case "navigation-links": {
      return (
        <div className={`${styles["footer-menu"]}`}>
          <FooterMenu
            menu={footerMenu}
            headline={columnData.navigations_links_title}
          />
        </div>
      );
    }
    case "social-icons": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={columnData.social_links_title}
          />
        </div>
      );
    }
    case "social-icons-with-links": {
      return (
        <div className={`${styles["footer-socials"]}`}>
          <Socials
            platforms={socialMedia.platforms}
            headline={columnData.social_links_title}
            showName
          />
        </div>
      );
    }
    default: {
      return <></>;
    }
  }
};
const FooterDisclaimer = ({
  accordion,
}: {
  accordion: FooterAccordionType;
}) => {
  const { getIcon } = useGlobalContext();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles["footer-disclaimer"]}>
      <div
        className={styles["disclaimer-header"]}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setIsOpen(!isOpen);
          }
        }}
      >
        <span className={styles["disclaimer-title"]}>{accordion.title}</span>
        <CustomSVG
          src={getIcon("chevron-down")}
          className={`${styles.chevron} ${isOpen ? styles.open : ""} is-flex`}
          wrapper={"span"}
          width={10}
          height={10}
          aria-hidden={true}
        />
      </div>
      <div
        className={`${styles["disclaimer-wrapper"]} ${isOpen ? styles["open"] : ""}`}
      >
        <div className={styles["disclaimer-inner"]}>
          <div className="container p-4">
            <div className="columns is-centered">
              <div className="column is-12 is-10-widescreen is-8-fullhd">
                <div
                  id="footer-disclaimer-content"
                  className={styles["disclaimer-content"]}
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(accordion.content),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FooterPromoBox = ({ promobox }: { promobox: PromoBoxType }) => {
  return (
    <div
      className={styles["footer-promobox"]}
      style={
        {
          "--promo-box-bg": promobox.background_color,
        } as React.CSSProperties
      }
    >
      <div className={styles["promobox-logo"]}>
        <MediaItemResponsive
          image_desktop={promobox.logo}
          sizes="(max-width: 1023px) 100vw, 256px"
          className={styles["logo"]}
          objectFit="contain"
        />
      </div>
      <div
        className={styles["promobox-content"]}
        dangerouslySetInnerHTML={{ __html: customSanitize(promobox.content) }}
      />
      {promobox.link_extended && (
        <ExtendedLink
          extendedLink={promobox.link_extended}
          btnType="secondary"
          extraClass="is-fullwidth my-0"
          invert
        />
      )}
    </div>
  );
};

const DesktopFooter = ({
  legalMenu,
  footerMenu,
  socialMedia,
  footerOptions,
  autoShowConsentManagement,
}: FooterProps) => {
  const { getLabel } = useGlobalContext();
  // next/compat/router returns null outside the pages router (e.g. Storybook)
  const router = useRouter();
  const locale = router?.locale;
  const defaultLocale =
    router?.defaultLocale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? "de";

  const {
    webdesign,
    navigation,
    column_1,
    column_2,
    column_3,
    column_4,
    footer_structure,
    promobox,
    accordion,
  } = footerOptions || {};

  function calculateFooterColumnWidth() {
    let numberColumns = 0;
    if (footer_structure?.column_1?.show_column) numberColumns++;
    if (footer_structure?.column_2?.show_column) numberColumns++;
    if (footer_structure?.column_3?.show_column) numberColumns++;
    if (footer_structure?.column_4?.show_column) numberColumns++;
    else if (promobox) numberColumns++; // Count promobox as a column if column 4 is hidden (or just simply count it)
    if (numberColumns === 4) return "25%";
    if (numberColumns === 3) return "33.33%";
    if (numberColumns === 2) return "50%";
    if (numberColumns === 1) return "100%";
    return "100%";
  }

  return (
    <>
      {accordion && <FooterDisclaimer accordion={accordion} />}
      <footer
        className={`footer is-clipped ${styles.footer} ${navigation.footer_menu.show_line ? styles["footer-with-line"] : ""}`}
        aria-label="footer"
        id="kontakt"
        style={
          {
            "--footer-column-width": `${calculateFooterColumnWidth()}`,
          } as React.CSSProperties
        }
      >
        <ConsentManagement
          wpApiUrl={process.env.WP_API_URL!}
          autoShow={autoShowConsentManagement}
          reloadOnSave={true}
          forceShow={false}
          path={undefined}
          locale={locale && locale !== defaultLocale ? locale : undefined}
          cookieExpirationTimeMs={undefined}
        />
        <div className="container">
          <div className={`${styles["footer-top"]}`}>
            {footer_structure?.column_1?.show_column && column_1 && (
              <FooterColumn
                type={footer_structure.column_1.type}
                columnData={column_1}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
              />
            )}
            {footer_structure?.column_2?.show_column && column_2 && (
              <FooterColumn
                type={footer_structure.column_2.type}
                columnData={column_2}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
              />
            )}
            {footer_structure?.column_3?.show_column && column_3 && (
              <FooterColumn
                type={footer_structure.column_3.type}
                columnData={column_3}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
              />
            )}
            {footer_structure?.column_4?.show_column && column_4 ? (
              <FooterColumn
                type={footer_structure.column_4.type}
                columnData={column_4}
                footerMenu={footerMenu}
                socialMedia={socialMedia}
              />
            ) : (
              promobox && <FooterPromoBox promobox={promobox} />
            )}
          </div>

          <div className={`${styles["footer-bottom"]}`}>
            <div
              className={`${styles["webdesign"]} has-text-centered developed`}
              dangerouslySetInnerHTML={{ __html: customSanitize(webdesign) }}
            />
            {legalMenu && <LegalMenu menu={legalMenu} />}
          </div>
        </div>

        <Section
          tag="div"
          disableColumns
          className="backtotop"
          containerClassName="is-flex is-justify-content-flex-end"
        >
          {/* eslint-disable-next-line react/forbid-elements */}
          <a href="#pagestart" aria-label={getLabel("aria_label_back_to_top")}>
            <i className="arrow up"></i>
            {getLabel("back_to_top", "Back to top")}
          </a>
        </Section>
      </footer>
    </>
  );
};
